/*
  Web implementation of List: renders a semantic <ul> with direct <li> children
  so screen readers get valid list structure (no divs between ul and li).
  Virtualized lists scroll via ScrollView (no FlatList on web). Static lists
  (`isVirtualized={false}`) do not, so a nested scroll region does not hide the
  list (RN-3872). Item position uses aria-posinset and aria-setsize on each <li>.
*/

import type { ListItem, ListProps } from "./List.types";

interface WebProps {
  useCustomListItem?: boolean;
  containerProps?: React.ComponentProps<typeof View>;
  listItemProps?: React.ComponentProps<typeof View>;
}

import React, { useId } from "react";
import { ScrollView, View, type AccessibilityRole, StyleSheet, type ViewStyle } from "react-native";

export function List({
  data,
  testID,
  style,
  a11yLabels,
  nativeID,
  ListHeaderComponent,
  ListFooterComponent,
  containerProps,
  listItemProps,
  useCustomListItem = false,
  scrollEnabled,
  isVirtualized = true,
  ...rest
}: ListProps & WebProps) {
  const defaultTestId = `list-test-${useId()}`;
  const listTestId = testID ?? defaultTestId;
  const { description } = a11yLabels ?? {};
  const totalItems = data.length;

  const listHeader =
    ListHeaderComponent != null ? (
      typeof ListHeaderComponent === "function" ? (
        <ListHeaderComponent />
      ) : (
        ListHeaderComponent
      )
    ) : null;
  const listFooter =
    ListFooterComponent != null ? (
      typeof ListFooterComponent === "function" ? (
        <ListFooterComponent />
      ) : (
        ListFooterComponent
      )
    ) : null;

  const list = (
    <View
      nativeID={nativeID}
      testID={`container-${listTestId}`}
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      accessibilityRole={"list" as unknown as AccessibilityRole}
      {...containerProps}
      accessibilityLabel={description ?? containerProps?.accessibilityLabel}
      style={[styles.list, containerProps?.style, rest.contentContainerStyle]}
    >
      {data.map((item: ListItem, index: number) =>
        useCustomListItem ? (
          <React.Fragment key={item.key}>{item.content}</React.Fragment>
        ) : (
          <View
            key={item.key}
            {...{
              "aria-posinset": index + 1,
              "aria-setsize": totalItems,
            }}
            {...listItemProps}
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
            accessibilityRole={"listitem" as unknown as AccessibilityRole}
          >
            {item.content}
          </View>
        ),
      )}
    </View>
  );

  if (!isVirtualized) {
    return (
      <View style={style} testID={listTestId} onLayout={rest.onLayout}>
        {listHeader}
        {list}
        {listFooter}
      </View>
    );
  }

  return (
    <ScrollView
      style={[styles.scrollView, style]}
      contentContainerStyle={styles.contentContainer}
      testID={listTestId}
      scrollEnabled={scrollEnabled}
      {...rest}
      pointerEvents={scrollEnabled === false ? "box-none" : rest.pointerEvents}
    >
      {listHeader}
      {list}
      {listFooter}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  list: {
    margin: 0,
    padding: 0,
    listStyle: "none",
  } as ViewStyle,
});
